import React from 'react';
import { VideoTrack, isTrackReference, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { Box, Text } from '@gluestack-ui/themed';

import {
  LabStandInImage,
  useOralExamLabOverrides,
} from '@/common/oralExam/oralExamLabOverrides';

import {
  SELF_VIEW_CHIP_HEIGHT_PX,
  SELF_VIEW_CHIP_INSET_PX,
  SELF_VIEW_CHIP_WIDTH_PX,
} from './selfViewTile';

type SelfViewChipProps = {
  sharing?: boolean;
  testID?: string;
};

const FILL_VIDEO_STYLE = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%' as const,
  height: '100%' as const,
  objectFit: 'cover' as const,
};

/**
 * Small picture-in-picture chip showing the student's own camera feed.
 * Anchored bottom-right of its parent (question column). Badge: "You" by
 * default, "Sharing" while screen share is active (mockup q-active-speaking).
 * Returns null when no local camera track is available.
 */
export function SelfViewChip({ sharing = false, testID = 'oral-exam-self-view-chip' }: SelfViewChipProps) {
  const lab = useOralExamLabOverrides();
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
    { onlySubscribed: false },
  );

  const localCameraTrack = cameraTracks.find(
    (tr) => tr.participant.isLocal && isTrackReference(tr),
  );
  const labSelfView = lab?.selfViewImageUrl ?? null;

  if (!labSelfView && (!localCameraTrack || !isTrackReference(localCameraTrack))) return null;

  return (
    <Box
      testID={testID}
      position="absolute"
      bottom={SELF_VIEW_CHIP_INSET_PX}
      right={SELF_VIEW_CHIP_INSET_PX}
      width={SELF_VIEW_CHIP_WIDTH_PX}
      height={SELF_VIEW_CHIP_HEIGHT_PX}
      borderRadius="$md"
      overflow="hidden"
      bg="$bgDark"
      zIndex={2}
    >
      {labSelfView != null ? (
        <LabStandInImage source={labSelfView} testID="patrol-lab-standin-self-view" />
      ) : localCameraTrack && isTrackReference(localCameraTrack) ? (
        <VideoTrack trackRef={localCameraTrack} style={FILL_VIDEO_STYLE} />
      ) : null}
      <Box
        testID="oral-exam-self-view-rec-dot"
        position="absolute"
        top={6}
        left={6}
        w={8}
        h={8}
        borderRadius="$full"
        bg="$statusError"
      />
      <Box
        testID={sharing ? 'oral-exam-screenshare-indicator' : 'oral-exam-self-view-you-badge'}
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        bg="$bgDark"
        opacity={0.85}
        px="$2"
        py={3}
        flexDirection="row"
        alignItems="center"
      >
        <Text size="2xs" color="$textInverted" fontWeight="semibold">
          {sharing ? 'Sharing' : 'You'}
        </Text>
      </Box>
    </Box>
  );
}
